import React from "react";
import { useAuth } from "./contexts/AuthContext";
import { __route, __ } from "localisation";
import { useLocation } from 'react-router-dom';
import { Switch } from "react-router-dom";
import { ScrollToTop } from "helpers";
import Layout from "./views/layouts/Layout";
import LayoutDefault from "./views/layouts/LayoutDefault";
import ClientLayout from "./views/layouts/ClientLayout";
import LayoutHeaderMenu from "./views/layouts/LayoutHeaderMenu";
import NoMatch from "views/NoMatch";
import IdleTimer from "./components/IdleTimer";

import LoggedOut from "./views/LoggedOut";
import Dashboard from './views/Dashboard';
import LoginFormTFA from 'views/LoginFormTFA';
import Clients from './views/Clients';
import Client from '../../packages/views/Client';
import Settings from './views/Settings';
import ResetPassword from './views/ResetPassword';
import ChangePassword from './views/ChangePassword';

import SourceOfFunds from "./views/SourceOfFunds";
import Files from './views/Files';
import CreateTestApproval from "./views/CreateTestApprovals";
import ApprovalsHubinsBackoffice from "./views/NewApprovals/ApprovalsHubinsBackoffice";
import ApprovalsHubinsCompliance from "./views/NewApprovals/ApprovalsHubinsCompliance";
import ApprovalsQuantumBackoffice from "./views/NewApprovals/ApprovalsQuantumBackoffice";
import ApprovalsQuantumCompliance from "./views/NewApprovals/ApprovalsQuantumCompliance";
import ApprovalsFilter from "./views/NewApprovals/ApprovalsFilter";
import ApprovalsArchived from "./views/NewApprovals/ApprovalsArchived";
import ApprovalsCompleted from "./views/NewApprovals/ApprovalsCompleted";
import NewApproval from "./views/NewApproval";
import ROYCList from "./views/ROYCList";
import Profiles from "./views/Profiles";
import Profile from "./views/Profile";
import PendingTofList from "./views/PendingTofList";
import OrdersTransactions from "./views/Orders/OrdersTransactions";
import OrdersFaTransactions from "./views/Orders/OrdersFaTransactions";
import OrdersCreateTof from "./views/Orders/OrdersCreateTof";

import RiskProfile from "./views/RiskProfile";

import Batch from "./views/Batch";
import Users from "./views/Users";

import DynamicLoadPage from '@hubins/components/DynamicLoadPage';

const RiskAssessmentParams = DynamicLoadPage({
  loader: () => import('./views/RiskAssessmentParams')
});

const ReportGenerator = DynamicLoadPage({
  loader: () => import('./views/ReportGenerator')
});

const CreatePortfolioOrProfile = DynamicLoadPage({
  loader: () => import('./views/CreatePortfolioOrProfile')
});

const Alerts = DynamicLoadPage({
  loader: () => import('./views/Alerts')
});

const Routes = ({ children }) => {
  const {
    userRoles,
    authToken,
    updateUser,
    user,
    tokenExpires,
    logout,
    refreshToken
  } = useAuth();

  return (
    <>
      {authToken && (
        <IdleTimer
          authToken={authToken}
          tokenExpires={tokenExpires}
          logoutUser={logout}
          refreshToken={refreshToken}
        />
      )}
      {children}
      <ScrollToTop location={useLocation} />
      <Switch>
        <Layout
          layout={LayoutHeaderMenu}
          title="Login"
          exact
          path={__route("root")}
          component={(props) => (
            <LoginFormTFA
              reset
              redirectTo="dashboard"
              authToken={authToken}
              updateUser={updateUser}
              {...props}
            />
          )}
          auth={false}
        />
        <Layout
          layout={LayoutHeaderMenu}
          title={__('reset_password_header')}
          exact
          path={'/reset'}
          component={ResetPassword}
          auth={false}
        />
        <Layout
          layout={LayoutHeaderMenu}
          title={__('reset_password_header')}
          exact
          path={'/reset/password/:token'}
          component={ChangePassword}
          auth={false}
        />
        <Layout
          layout={LayoutHeaderMenu}
          title={"ROYC registrations"}
          exact
          path={'/royc'}
          component={ROYCList}
        />
        <Layout
          layout={LayoutDefault}
          title="TEST"
          exact
          path="/test/create-approval"
          component={CreateTestApproval}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/dashboard"
          component={Dashboard}
        />
        <Layout
          layout={LayoutDefault}
          title="Settings"
          exact
          path="/dashboard/settings"
          component={Settings}
        />
        <Layout
          layout={LayoutDefault}
          title="Approvals"
          exact
          path="/dashboard/approvals/hubins-backoffice"
          component={ApprovalsHubinsBackoffice}
        />
        <Layout
          layout={LayoutDefault}
          title="Approvals"
          exact
          path="/dashboard/approvals/hubins-compliance"
          component={ApprovalsHubinsCompliance}
        />
        <Layout
          layout={LayoutDefault}
          title="Approvals"
          exact
          path="/dashboard/approvals/quantum-leben-backoffice"
          component={ApprovalsQuantumBackoffice}
        />
        <Layout
          layout={LayoutDefault}
          title="Approvals"
          exact
          path="/dashboard/approvals/quantum-leben-compliance"
          component={ApprovalsQuantumCompliance}
        />
        <Layout
          layout={LayoutDefault}
          title="Approvals"
          exact
          path="/dashboard/approvals/filter"
          component={ApprovalsFilter}
        />
        <Layout
          layout={LayoutDefault}
          title="Approvals"
          exact
          path="/dashboard/approvals/archived"
          component={ApprovalsArchived}
        />
        <Layout
          layout={LayoutDefault}
          title="Approvals"
          exact
          path="/dashboard/approvals/completed"
          component={ApprovalsCompleted}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/dashboard/approvals/approval/:sourceUuid"
          component={NewApproval}
          noSpacing
        />
        <Layout
          layout={LayoutDefault}
          title="Clients"
          exact
          path="/dashboard/clients"
          component={Clients}
        />
        <Layout
          layout={LayoutDefault}
          title="Users"
          exact
          path="/dashboard/profiles"
          component={Profiles}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/dashboard/profiles/:profileUuid"
          render={(props) => <Profile {...props} userRoles={userRoles} />}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/sof/create/:type/:profileUuid"
          component={SourceOfFunds}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/sof/edit/:type/:sourceId/:profileUuid"
          component={SourceOfFunds}
        />
        <Layout
          layout={ClientLayout}
          title="Clients"
          exact
          withClient
          path="/dashboard/clients/:profileUuid/:portfolioUuid/tabs/:tab?"
          render={(props) => {
            return <Client userRoles={userRoles} {...props} />;
          }}
        />
        <Layout
          layout={LayoutDefault}
          title="Riskprofil"
          exact
          path="/portfolios/:portfolioUuid/risk-profile"
          component={RiskProfile}
        />
        <Layout
          layout={LayoutHeaderMenu}
          title={__("logging_out")}
          exact
          path={__route("logout")}
          auth={false}
          component={LoggedOut}
        />
        {/* Files route */}
        <Layout
          layout={LayoutDefault}
          title="Files"
          exact
          path="/dashboard/files"
          component={Files}
        />

        <Layout
          layout={LayoutDefault}
          exact
          path="/dashboard/pending-fa-transactions"
          component={PendingTofList}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/dashboard/orders/transactions"
          title="Orders"
          component={OrdersTransactions}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/dashboard/orders/fa-transactions"
          title="Orders"
          component={OrdersFaTransactions}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/dashboard/orders/create-tof"
          title="Orders"
          component={OrdersCreateTof}
        />
        <Layout
          layout={LayoutDefault}
          exact
          path="/dashboard/batches/:batchId"
          component={Batch}
        />
        <Layout
          layout={LayoutDefault}
          path="/db/users"
          title="Users"
          component={(props) => <Users userRoles={userRoles} {...props} />}
        />
        <Layout
          layout={LayoutDefault}
          title="Risk assessment params"
          path="/dashboard/risk-assessment-params"
          component={RiskAssessmentParams}
        />
        <Layout
          layout={LayoutDefault}
          path="/dashboard/report-generator"
          title="Rapport"
          component={ReportGenerator}
        />
        <Layout
          layout={LayoutDefault}
          path="/dashboard/create"
          component={CreatePortfolioOrProfile}
        />
        <Layout
          layout={LayoutDefault}
          path="/dashboard/alerts"
          title="Notifications"
          component={Alerts}
        />
        <Layout
          layout={LayoutHeaderMenu}
          title="404 - Not found"
          component={NoMatch}
          btnType="logout"
        />

      </Switch>
    </>
  );
};

export default Routes;
